import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Sparkles } from "lucide-react";
import { api, formatCHF, formatDate, formatTime } from "@/lib/api";
import { BookingDialog } from "@/components/BookingDialog";
import { Reveal } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SOINS_COMPARATIF, SOINS_ETAPES, SOINS_FAQ } from "@/data/soins";
import { BtnNeSaisPas } from "@/components/SoinsChoisir";

export const SoinsComparatif = () => (
  <section className="bg-sand-dark/50 border-y border-sand-dark" data-testid="soins-comparatif">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
      <Reveal className="max-w-2xl mb-10">
        <p className="eyebrow mb-6">Comparatif</p>
        <h2 className="font-serif text-3xl sm:text-4xl text-bark leading-tight">Les formules et leurs tarifs, en un coup d'œil.</h2>
      </Reveal>
      <Reveal>
        <table className="hidden md:table w-full text-sm bg-card border border-sand-dark rounded-2xl overflow-hidden" data-testid="soins-comparatif-table">
          <thead><tr className="font-mono text-[10px] uppercase tracking-[0.2em] text-fumee text-left border-b border-sand-dark">{["Porte", "Formule", "Pour quelle situation", "Déroulement", "Tarif"].map((h) => <th key={h} className="px-5 py-4 font-normal">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-sand-dark">
            {SOINS_COMPARATIF.map((r) => (
              <tr key={r.formule} data-testid={`comparatif-${r.porte}-${r.formule.slice(0, 8).toLowerCase().replace(/[^a-z]/g, "")}`}>
                <td className="px-5 py-4 font-serif text-2xl text-terra/80">{r.porte}</td>
                <td className="px-5 py-4 font-serif text-lg text-bark">{r.formule}</td>
                <td className="px-5 py-4 text-bark/80">{r.pour}</td>
                <td className="px-5 py-4 text-fumee">{r.format}</td>
                <td className="px-5 py-4 font-serif text-lg text-terra whitespace-nowrap">{r.prix}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <ul className="md:hidden space-y-3" data-testid="soins-comparatif-mobile">
          {SOINS_COMPARATIF.map((r) => (
            <li key={r.formule} className="bg-card border border-sand-dark rounded-2xl p-5 flex gap-4">
              <span className="font-serif text-3xl text-terra/80 leading-none">{r.porte}</span>
              <div className="flex-1">
                <div className="flex justify-between gap-3"><p className="font-serif text-lg text-bark leading-tight">{r.formule}</p><span className="font-serif text-terra whitespace-nowrap">{r.prix}</span></div>
                <p className="text-sm text-bark/80 mt-1.5">{r.pour}</p>
                <p className="text-xs text-fumee mt-1">{r.format}</p>
              </div>
            </li>
          ))}
        </ul>
      </Reveal>
    </div>
  </section>
);

export const SoinsDeroulement = () => (
  <section className="bg-earth grain text-sand relative" data-testid="soins-deroulement">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 relative z-[2]">
      <Reveal>
        <p className="eyebrow mb-6">Comment se déroule un soin ?</p>
        <h2 className="font-serif text-3xl sm:text-4xl leading-tight max-w-xl mb-14">Quatre étapes, toujours les mêmes.</h2>
      </Reveal>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
        {SOINS_ETAPES.map((e, i) => (
          <Reveal key={e.titre} delay={i * 0.1}>
            <div className="border-t border-sand/20 pt-6" data-testid={`soins-etape-${i}`}>
              <span className="font-serif text-5xl text-terra/80">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="font-serif text-xl mt-4 mb-3">{e.titre}</h3>
              <p className="text-sm text-sand/65 leading-relaxed">{e.texte}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const SoinsFaq = () => (
  <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24 grid lg:grid-cols-12 gap-12" data-testid="soins-faq">
    <Reveal className="lg:col-span-4">
      <p className="eyebrow mb-6">Questions fréquentes</p>
      <h2 className="font-serif text-3xl sm:text-4xl text-bark leading-tight">Ce que l'on nous demande souvent.</h2>
      <p className="text-fumee text-sm leading-relaxed mt-4">D'autres questions ? La <Link to="/faq" className="link-line text-terra" data-testid="soins-faq-lien">FAQ générale</Link> ou un message suffisent.</p>
      <BtnNeSaisPas className="mt-8" testid="soins-ne-sais-pas-faq" />
    </Reveal>
    <Reveal delay={0.1} className="lg:col-span-8">
      <Accordion type="single" collapsible className="w-full border-t border-sand-dark">
        {SOINS_FAQ.map((f, i) => (
          <AccordionItem key={f.q} value={`s-${i}`} className="border-sand-dark" data-testid={`soins-faq-item-${i}`}>
            <AccordionTrigger className="font-serif text-lg text-bark text-left hover:text-terra hover:no-underline py-5">{f.q}</AccordionTrigger>
            <AccordionContent className="text-fumee leading-relaxed text-sm pb-5">{f.r}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </Reveal>
  </section>
);

export const SoinDuJour = () => {
  const [soins, setSoins] = useState(null);
  const [booking, setBooking] = useState(null);
  useEffect(() => { api.events({ type: "soin" }).then((l) => setSoins(l.filter((e) => !e.annule && new Date(e.date_fin || e.date) >= new Date()))).catch(() => setSoins([])); }, []);
  const soin = soins?.[0];
  const complet = soin && !soin.places_illimitees && soin.places_restantes <= 0;
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16" data-testid="soin-du-jour">
      {soin ? (
        <Reveal className="relative overflow-hidden bg-earth grain text-sand border border-terra/40 rounded-2xl p-6 lg:p-10 flex flex-col lg:flex-row lg:items-center justify-between gap-8" data-testid="soin-du-jour-actif">
          <div className="relative z-[2] max-w-2xl">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-terra mb-3 inline-flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> Le soin du jour · soin collectif à distance</p>
            <h3 className="font-serif text-2xl lg:text-3xl leading-tight">{soin.titre}</h3>
            <p className="mt-4 text-sm text-sand/75 leading-relaxed">{soin.description}</p>
            <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-sand/60 inline-flex flex-wrap items-center gap-x-4 gap-y-1">
              <span className="inline-flex items-center gap-2 capitalize"><CalendarDays className="w-3.5 h-3.5 text-terra" />{soin.date_label || `${formatDate(soin.date)} · ${formatTime(soin.date)}`}</span>
              {soin.duree && <span>· {soin.duree}</span>}
              <span>· {complet ? "complet" : soin.places_illimitees ? "places illimitées" : `${soin.places_restantes} place${soin.places_restantes > 1 ? "s" : ""}`}</span>
            </p>
          </div>
          <div className="relative z-[2] flex flex-col gap-3 lg:items-end shrink-0">
            {soin.prix != null && <p className="font-serif text-2xl text-terra">{formatCHF(soin.prix)} <span className="text-xs text-sand/60 font-sans">par personne</span></p>}
            <button type="button" onClick={() => setBooking(soin)} disabled={complet} data-testid="soin-du-jour-participer"
              className="px-7 py-3 rounded-full bg-terra text-earth text-sm tracking-wide hover:bg-sand transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed">{complet ? "Complet" : "Participer au soin"}</button>
            <Link to="/soin" className="text-xs text-sand/60 link-line" data-testid="soin-du-jour-page">Page du soin du moment{soins.length > 1 ? ` · ${soins.length - 1} soin${soins.length > 2 ? "s" : ""} à venir` : ""}</Link>
          </div>
          <BookingDialog event={booking} open={!!booking} onOpenChange={(v) => !v && setBooking(null)} />
        </Reveal>
      ) : (
        <Reveal className="border border-sand-dark rounded-2xl p-6 lg:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="max-w-2xl">
            <p className="eyebrow mb-3">Et aussi · Le soin du jour</p>
            <p className="font-serif text-xl text-bark leading-tight">Des soins collectifs à distance, aux moments-clés de l'année, à activer vous-même pendant 7 jours.</p>
          </div>
          <div className="flex flex-col gap-2 md:items-end shrink-0">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-fumee inline-flex items-center gap-2"><CalendarDays className="w-3.5 h-3.5" />{soins === null ? "Chargement…" : "Aucun soin collectif programmé"}</p>
            <Link to="/soin" className="text-sm text-terra link-line" data-testid="soin-du-jour-lettre">Page du soin du moment</Link>
          </div>
        </Reveal>
      )}
    </section>
  );
};
